import { useEffect, useReducer, useRef } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Demo hidup yang memeragakan Deteksi Otomatis di hero.
 *
 * Alasannya sederhana: janji utama produk ini - "tempel URL, kolomnya terisi
 * sendiri" - sulit dipercaya kalau hanya dibaca. Tangkapan layar statis pun
 * tidak menunjukkan bahwa prosesnya cepat. Peragaan yang berjalan sendiri
 * membuat pengunjung *melihat* janji itu terjadi dalam empat detik.
 *
 * Semua data di sini contoh yang ditulis di muka - tidak ada panggilan
 * jaringan sama sekali, jadi halaman depan tidak pernah menunggu apa pun.
 */

const URL_CONTOH = 'https://tokopedia.com/elektronik';

const KOLOM = [
  { nama: 'Nama Produk', tipe: 'Teks', contoh: 'Logitech MX Master 3S' },
  { nama: 'Harga', tipe: 'Angka', contoh: 'Rp 1.349.000' },
  { nama: 'Rating', tipe: 'Angka', contoh: '4,9' },
  { nama: 'Terjual', tipe: 'Angka', contoh: '2.847' },
  { nama: 'Toko', tipe: 'Teks', contoh: 'Logitech Official' },
];

/** Tahapan peragaan, beserta lama tiap tahap dalam milidetik. */
const TAHAP = { MENGETIK: 0, MEMINDAI: 1, HASIL: 2, JEDA: 3 };

const reducer = (state, action) => {
  switch (action.type) {
    case 'ketik':
      return { ...state, ketikan: URL_CONTOH.slice(0, state.ketikan.length + 1) };
    case 'tahap':
      return { ...state, tahap: action.tahap, kolomTampil: 0 };
    case 'kolom':
      return { ...state, kolomTampil: Math.min(KOLOM.length, state.kolomTampil + 1) };
    case 'ulang':
      return { tahap: TAHAP.MENGETIK, ketikan: '', kolomTampil: 0 };
    default:
      return state;
  }
};

export const DetectionDemo = () => {
  const [state, dispatch] = useReducer(reducer, {
    tahap: TAHAP.MENGETIK,
    ketikan: '',
    kolomTampil: 0,
  });

  const timer = useRef(null);

  useEffect(() => {
    // Pengguna yang mematikan animasi di sistemnya melihat hasil akhirnya
    // langsung - tanpa gerakan sama sekali.
    const diam = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (diam) {
      dispatch({ type: 'tahap', tahap: TAHAP.HASIL });
      for (let i = 0; i < KOLOM.length; i += 1) dispatch({ type: 'kolom' });
      return undefined;
    }

    const { tahap, ketikan, kolomTampil } = state;
    let jeda = 0;
    let aksi = null;

    if (tahap === TAHAP.MENGETIK) {
      if (ketikan.length < URL_CONTOH.length) {
        jeda = 38;
        aksi = { type: 'ketik' };
      } else {
        jeda = 420;
        aksi = { type: 'tahap', tahap: TAHAP.MEMINDAI };
      }
    } else if (tahap === TAHAP.MEMINDAI) {
      jeda = 1250;
      aksi = { type: 'tahap', tahap: TAHAP.HASIL };
    } else if (tahap === TAHAP.HASIL) {
      if (kolomTampil < KOLOM.length) {
        jeda = 130;
        aksi = { type: 'kolom' };
      } else {
        jeda = 3200;
        aksi = { type: 'tahap', tahap: TAHAP.JEDA };
      }
    } else {
      jeda = 260;
      aksi = { type: 'ulang' };
    }

    timer.current = setTimeout(() => dispatch(aksi), jeda);
    return () => clearTimeout(timer.current);
  }, [state]);

  const { tahap, ketikan, kolomTampil } = state;
  const selesaiMengetik = ketikan.length === URL_CONTOH.length;

  return (
    <div className="relative">
      {/* Cahaya di belakang panel - memberi kedalaman tanpa bayangan berat */}
      <div
        className="pointer-events-none absolute -inset-8 rounded-[2rem] bg-[radial-gradient(ellipse_at_center,var(--color-signal-500),transparent_70%)] opacity-[0.12] blur-2xl"
        aria-hidden="true"
      />

      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[var(--color-void-900)]/90 shadow-2xl backdrop-blur">
        {/* Bilah jendela */}
        <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
          <span className="size-2.5 rounded-full bg-white/15" />
          <span className="size-2.5 rounded-full bg-white/15" />
          <span className="size-2.5 rounded-full bg-white/15" />
          <span className="ml-2 text-[11px] font-medium text-white/35">
            Crawel - Buat Scraping
          </span>
        </div>

        <div className="space-y-4 p-5">
          {/* Kolom URL */}
          <div>
            <p className="mb-1.5 text-[11px] font-bold tracking-wider text-white/40 uppercase">
              URL Sumber
            </p>
            <div className="flex items-center gap-2 rounded-lg border border-white/12 bg-black/25 px-3 py-2.5">
              {/* `break-all` supaya URL panjang membungkus, bukan mendorong
                  panel melebihi lebar layar di ponsel. */}
              <span className="min-w-0 font-mono text-[13px] break-all text-white/85">
                {ketikan}
              </span>
              {!selesaiMengetik && (
                <span className="animate-caret font-mono text-[13px] text-signal-400">▍</span>
              )}
            </div>
          </div>

          {/* Tombol deteksi */}
          <div
            className={cn(
              'relative flex items-center justify-center gap-2 overflow-hidden rounded-lg py-2.5',
              'text-sm font-bold transition-colors duration-300',
              selesaiMengetik
                ? 'bg-signal-400 text-void-950'
                : 'bg-white/8 text-white/30',
            )}
          >
            <Sparkles className="size-4" aria-hidden="true" />
            {tahap === TAHAP.MEMINDAI ? 'Menganalisa halaman…' : 'Deteksi Otomatis'}
            {tahap === TAHAP.MEMINDAI && (
              <span
                className="animate-sweep absolute inset-y-0 w-1/3 bg-white/25 blur-sm"
                aria-hidden="true"
              />
            )}
          </div>

          {/* Hasil */}
          <div className="min-h-[248px]">
            {tahap === TAHAP.MENGETIK && (
              <p className="pt-16 text-center text-sm text-white/25">
                Kolom akan terisi sendiri setelah dianalisa.
              </p>
            )}

            {tahap === TAHAP.MEMINDAI && (
              <div className="space-y-2 pt-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-9 animate-pulse rounded-lg bg-white/6"
                    style={{ animationDelay: `${i * 90}ms` }}
                  />
                ))}
              </div>
            )}

            {(tahap === TAHAP.HASIL || tahap === TAHAP.JEDA) && (
              <div className="space-y-2 pt-2">
                <div className="flex items-center gap-2 pb-1">
                  <Check className="size-4 text-signal-400" aria-hidden="true" />
                  <p className="text-sm text-white/70">
                    Ditemukan <span className="font-bold text-white">48 produk</span> dengan{' '}
                    <span className="font-bold text-white">5 kolom</span>
                  </p>
                </div>

                {KOLOM.slice(0, kolomTampil).map((kolom, i) => (
                  <div
                    key={kolom.nama}
                    className="animate-rise flex items-center gap-3 rounded-lg border border-white/8 bg-white/[0.04] px-3 py-2"
                    style={{ animationDelay: `${i * 25}ms` }}
                  >
                    <span className="w-28 shrink-0 truncate text-[13px] font-semibold text-white">
                      {kolom.nama}
                    </span>
                    <span className="shrink-0 rounded border border-white/12 px-1.5 py-0.5 text-[10px] font-bold text-white/45">
                      {kolom.tipe}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-right font-mono text-[11px] text-white/40">
                      {kolom.contoh}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
